import { useState } from "react";

import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "@rook/shared/lib/cn";
import {
  Badge as UiBadge,
  Button,
  SidebarNav,
  SidebarNavItem,
  type BadgeProps,
} from "@rook/ui";

import {
  getNavItems,
  type ChildNavItem,
  type ExpandableNavItem,
  type FlatNavItem,
  type NavBadge,
} from "./navigation";
import { useNavStatus } from "./useNavStatus";

/**
 * Amber attention dot rendered next to nav rows whose target view
 * still needs configuration (Webhook endpoint blank, Timezone on
 * UTC default, SDK keys empty, Data Structure not customized).
 * Same look at every nesting level — single source of truth.
 */
const AttentionDot = () => (
  <span
    aria-hidden
    title="Needs configuration"
    className="ml-auto inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400 shadow-[0_0_4px_rgba(251,191,36,0.6)]"
  />
);

// Maps a nav badge kind to the canonical @rook/ui Badge variant.
const NAV_BADGE_VARIANT: Record<NavBadge, BadgeProps["variant"]> = {
  beta: "info",
  soon: "warning",
  enterprise: "success",
};

const Badge = ({ kind }: { kind: NavBadge }) => (
  <UiBadge
    variant={NAV_BADGE_VARIANT[kind]}
    className="ml-auto text-[9px] tracking-widest"
  >
    {kind}
  </UiBadge>
);

// Active-nav icon tint (the brand purple). Shared by Flat / Child / Group
// rows so the highlight stays consistent (and to avoid the duplicate-string
// smell lint flags on repeated literal classnames).
const ACTIVE_ICON_COLOR = "text-rook-purple";

/**
 * Top-level row. Rendered through `<SidebarNavItem asChild>` so the
 * react-router `<Link>` is the actual anchor and the icon + label live
 * INSIDE it (the asChild contract). The design system owns the row
 * chrome (padding, hover, active background); we only supply the
 * concept icon, label, badge/attention affordances, and the tour hook.
 */
const FlatRow = ({
  item,
  isActive,
  needsAttention,
}: {
  item: FlatNavItem;
  isActive: boolean;
  needsAttention: boolean;
}) => (
  <SidebarNavItem asChild active={isActive}>
    <Link to={item.path} data-tour={item.dataTour}>
      <item.Icon
        size={17}
        className={cn(isActive && ACTIVE_ICON_COLOR)}
        strokeWidth={1.6}
      />
      <span>{item.title}</span>
      {item.badge ? <Badge kind={item.badge} /> : null}
      {needsAttention && !item.badge ? <AttentionDot /> : null}
    </Link>
  </SidebarNavItem>
);

/**
 * Child row inside an expandable group. Same `<SidebarNavItem asChild>`
 * anchor contract as FlatRow, just a hair smaller/quieter via the
 * tighter icon size to read as a nested item.
 */
const ChildRow = ({
  child,
  isActive,
  needsAttention,
}: {
  child: ChildNavItem;
  isActive: boolean;
  needsAttention: boolean;
}) => (
  <SidebarNavItem asChild active={isActive}>
    <Link to={child.path}>
      <child.Icon
        size={15}
        className={cn(isActive && ACTIVE_ICON_COLOR)}
        strokeWidth={1.6}
      />
      <span>{child.title}</span>
      {child.badge ? <Badge kind={child.badge} /> : null}
      {needsAttention && !child.badge ? <AttentionDot /> : null}
    </Link>
  </SidebarNavItem>
);

/**
 * Expandable group header + its children. The group header is NOT a
 * navigable link (it toggles open/closed), so it does NOT map cleanly
 * to `<SidebarNavItem asChild><Link/>`; it stays a minimal local
 * `<Button>` wrapper styled to sit flush with the SidebarNav rows.
 * The CHILDREN, however, are rendered through SidebarNavItem (ChildRow)
 * so the nested rows match the design-system standard.
 *
 * Preserves: open-by-default-when-inside-group, the group attention
 * dot when collapsed, the `data-tour` hook, and the regex active match.
 */
const ExpandableRow = ({
  item,
  pathname,
  attentionByPath,
}: {
  item: ExpandableNavItem;
  pathname: string;
  attentionByPath: Record<string, boolean>;
}) => {
  const groupActive = item.matchRegex.test(pathname);
  // Open by default if we're inside the group; user can collapse
  // afterwards. Local state — doesn't persist across nav.
  const [open, setOpen] = useState(groupActive);

  // If ANY child of this collapsed group needs attention, surface
  // the dot on the group row so the customer notices without
  // expanding. Suppressed when open (the child dots are visible).
  const groupNeedsAttention =
    !open && item.children.some((c) => attentionByPath[c.path]);

  return (
    <li>
      <Button
        type="button"
        variant={null}
        size={null}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-tour={item.dataTour}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-light text-rook-stone/80 transition-colors hover:bg-white/5 hover:text-rook-stone",
          groupActive && "bg-white/[0.06] text-rook-stone",
        )}
      >
        <item.Icon
          size={17}
          className={cn(groupActive && ACTIVE_ICON_COLOR)}
          strokeWidth={1.6}
        />
        <span>{item.title}</span>
        {groupNeedsAttention ? <AttentionDot /> : null}
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 text-rook-olive transition-transform",
            !groupNeedsAttention && "ml-auto",
            open && "rotate-90",
          )}
          strokeWidth={2}
        />
      </Button>
      {open && (
        <ul className="mt-0.5 ml-4 space-y-0.5 border-l border-white/10 pl-3">
          {item.children.map((child) => (
            <ChildRow
              key={child.path}
              child={child}
              isActive={child.regex.test(pathname)}
              needsAttention={!!attentionByPath[child.path]}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/**
 * Primary navigation, rendered through the @rook/ui `<SidebarNav>`
 * primitive. Flat + child rows are `<SidebarNavItem asChild>` anchors
 * (react-router Links); expandable group headers keep a minimal local
 * toggle wrapper (they're not links) with SidebarNavItem-style children.
 *
 * Lives inside AppShell's `nav` slot (see HomeShell). The guided-tour
 * `data-tour` hooks and regex active-matching are preserved verbatim.
 */
export const Sidebar = () => {
  const { pathname } = useLocation();
  const { attentionByPath } = useNavStatus();
  // Includes the gated Billing row only when the ?designShowcase unlock
  // is set; default nav is unchanged.
  const items = getNavItems();

  return (
    <SidebarNav>
      {items.map((item) => {
        if (item.kind === "flat") {
          return (
            <FlatRow
              key={item.path}
              item={item}
              isActive={item.regex.test(pathname)}
              needsAttention={!!attentionByPath[item.path]}
            />
          );
        }
        return (
          <ExpandableRow
            key={item.title}
            item={item}
            pathname={pathname}
            attentionByPath={attentionByPath}
          />
        );
      })}
    </SidebarNav>
  );
};
